/**
 * src/lib/featureFlags.js — centralized feature-flag reads.
 *
 * Keep flags small and temporary. Delete a flag once the feature ships to all.
 *
 * Conventions:
 * - Every flag is a function: isXxxEnabled(profile) -> boolean.
 * - Accepts the user_profiles row from useAuth().profile (may include role,
 *   display_name, etc.) OR the supabase auth user (has .email).
 * - localStorage override with the flag name (e.g. 'finance_close_out_v1')
 *   set to '1' lets non-CEO accounts opt in for dogfood.
 *
 * Active flags:
 * - finance_close_out_v1 — Close-Out attention card + chase list.
 *     Ships CEO-only Phase 2; flag removed in Phase 3 exit.
 * - offline_tasks_v1 — Offline-first Tasks (local cache + outbox + sync-back).
 *     CEO-only while proving on real flights; localStorage override for dogfood.
 * - step4_shop_execute_v1 — TICKET-070 Step 4 "execute & report": out-of-scope
 *     task flag + authorization trail + field-log create. CEO-only; localStorage
 *     override for dogfood. (Backend env STEP4_SHOP_EXECUTE_V1 gates the server.)
 */

function hasLocalOverride(flagName) {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem(flagName) === '1';
  } catch {
    // SSR / hostile env / disabled storage — treat as no override
    return false;
  }
}

function isCeo(profile) {
  if (!profile) return false;
  if (profile.role === 'ceo') return true;
  if (profile.email === 'info@acnautitech.com') return true;
  return false;
}

export function isFinanceCloseOutV1Enabled(profile) {
  if (isCeo(profile)) return true;
  if (hasLocalOverride('finance_close_out_v1')) return true;
  return false;
}

export function isOfflineTasksEnabled(profile) {
  if (isCeo(profile)) return true;
  if (hasLocalOverride('offline_tasks_v1')) return true;
  return false;
}

export function isStep4ShopExecuteEnabled(profile) {
  if (isCeo(profile)) return true;
  if (hasLocalOverride('step4_shop_execute_v1')) return true;
  return false;
}

// cushion_calculator_v1 — TICKET-255 foam & cushion cost calculator + fabric
// estimator inside the Quote Builder. CEO + PMO (Agustina runs quoting/production);
// localStorage override for dogfood. Backend/UI gate on this flag.
// TICKET-318 (CEO 2026-07-20): grant PMO — Agustina needs the foam calculator.
export function isCushionCalculatorV1Enabled(profile) {
  if (isCeo(profile)) return true;
  if (profile?.role === 'pmo') return true;
  if (hasLocalOverride('cushion_calculator_v1')) return true;
  return false;
}

// runner_calculator_v1 — TICKET-441 runner / m2-covering configurator (Vertisol
// vs Sunbrella acrylic) inside the Quote Builder. Same audience as the cushion
// calculator: CEO + PMO quote from it, localStorage override for dogfood.
export function isRunnerCalculatorV1Enabled(profile) {
  if (isCeo(profile)) return true;
  if (profile?.role === 'pmo') return true;
  if (hasLocalOverride('runner_calculator_v1')) return true;
  return false;
}
